import { useContext, useMemo } from 'react';

import { ContentContext } from 'context/ContentContext';

import type { FeatureGridItem } from 'ui/atoms/FeatureGrid/FeatureGrid';
import { type Testimonial, Testimonials } from 'ui/organisms/Testimonials/Testimonials';
import { type DataCases, UseCases } from 'ui/organisms/UseCases/UseCases';
import { Hero } from 'ui/organisms/Hero/Hero';
import { VideoOverview } from 'ui/organisms/VideoOverview/VideoOverview';
import { BlockStandards } from 'ui/molecules/BlockStandards/BlockStandards';
import { OurCustomersV2 } from 'ui/molecules/OurCustomers/OurCustomersV2';
import { OurAwards } from 'ui/molecules/OurAwards/OurAwards';
import { StatsBlock } from 'ui/molecules/StatsBlock/StatsBlock';
import { InfoBlocks, type InfoItem } from 'ui/molecules/InfoBlocks/InfoBlocks';
import { AdvantageBlock } from 'ui/organisms/AdvantageBlock/AdvantageBlock';

import { PricingBlock } from 'features/PAYG/organisms/PricingBlock/PricingBlock';
import { SalesBanner } from 'features/PAYG/organisms/SalesBanner/SalesBanner';

import styles from './PAYGPage.module.scss';

import { ComparisonTable } from '../../organisms/ComparisonTable/ComparisonTable';
import { CompanyProfile } from '../../organisms/CompanyProfile/CompanyProfile';
import { Faq } from '../../organisms/Faq';
import { PAYG_SECOND_PAGE_FAQ_DATA } from '../PAYGSecondPage/faqData';

const baseClass = 'PAYG-page';

const VIDEO_ID = '0kj0iMuZ3iA';

const dataCases: DataCases[] = [
  {
    title: 'Direct from Source',
    description:
      'We source company data from 200+ official government registries—no third parties—ensuring top accuracy, traceability, and compliance.',
    icon: 'target',
  },
  {
    title: 'Full Commercial Rights',
    description: 'Build products, enhance services, resell data. No restrictions on how you use your intelligence.',
    icon: 'unlock',
  },
  {
    title: 'Price Lock Guarantee',
    description:
      'Your monthly rate stays the same. No surprise price increases year after year like traditional data brokers.',
    icon: 'lightning',
  },
  {
    title: 'Modern, Fast Platform',
    description:
      'Built for 2025 workflows. Intuitive interface designed for everyone, not just analysts. Quick onboarding and instant support.',
    icon: 'rocket',
  },
  {
    title: 'Trusted by Innovators',
    description: 'Used by LSEG, Statista, AMEX, WNS and forward-thinking teams replacing legacy data providers.',
    icon: 'check-5',
  },
  {
    title: 'Your Strategic Data Partner',
    description:
      "We adapt to your use case, sourcing new data when required — because real partnerships go beyond what's in the database.",
    icon: 'partner',
  },
];

const dataTestimonial: Testimonial[] = [
  {
    quote:
      "When you're resolving entities and mapping risk across borders, the foundation has to be authoritative and traceable — a registry-sourced record we can stand behind when a decision is questioned. Global Database gives us that across the markets we're expanding into, and their team absorbs the registry-level differences and constant changes, so ours stays focused on the intelligence layer rather than the data plumbing.",
    name: 'Adam Mulliken',
    role: 'CPO, Quantifind',
  },
  {
    quote:
      "We use Global Database's credit risk data when verifying and onboarding customers. It gives our teams the company and financial information they need to assess risk, complete checks and make decisions with confidence.",
    name: 'Carol Zhang',
    role: 'Associate Vice President, Kroll',
  },
  {
    quote:
      'We use Global Database to enrich our bank account validation and payee checks with business data. It helps us identify the company behind the payment and gives our customers more confidence before they pay.',
    name: 'Alexandre Outman',
    role: 'Lead Product Manager, Trustpair',
  },
  {
    quote:
      'Global Database helps us build a clear picture of our addressable market across the UK and Ireland. With more than 100 criteria available, we can segment companies precisely and map the right opportunities to each of our teams.',
    name: 'Cameron Trayner',
    role: 'Schneider Electric',
  },
];

const featuresHero: FeatureGridItem[] = [
  { icon: 'government', title: 'Government Sourced' },
  { icon: 'party-data', title: 'First Party Data' },
  { icon: 'globe', title: '300 Mil Company Profiles' },
];

const dataInfo: InfoItem[] = [
  {
    icon: 'search-by-window',
    title: 'Comprehensive Data',
    items: [
      'Access to 300M+ Company Profiles',
      'Global Group Structure',
      'Full company financials',
      'UBO (Ultimate Beneficial Owners)',
      'Firmographic data',
      'Local SIC Codes, NACE and industry tags',
      'Contact Data for key executives',
      'Website traffic and used technologies',
    ],
  },
  {
    icon: 'platform-features',
    title: 'Platform Features',
    items: [
      '100+ advanced search filters',
      'Bulk list upload & enrichment',
      'Powerful web extension',
      'Seamless API integration',
      'CRM compatibility with Salesforce',
      'Microsoft Dynamics integration',
      'Real-time data updates',
      'Modern REST API',
    ],
  },
  {
    icon: 'target',
    title: 'Usage Benefits',
    items: [
      'Unlimited online access',
      'Full commercial and resale rights',
      'Retain data ownership after contract expiration',
      'No geographic restrictions on resale',
      'No "Internal Use Only" restrictions',
      'No annual price increases',
      'No integration headaches',
      '24/7 technical support',
    ],
  },
];

const dataSwitchReasonInfo: InfoItem[] = [
  {
    icon: 'middleman-fees',
    title: 'Overpriced Middleman Fees',
    description:
      "Bureau van Dijk (Moody's) takes third-party data, repackages it, and charges premium prices. You're paying for the brand name, not better data.",
  },
  {
    icon: 'licensing',
    title: 'Restrictive Licensing',
    description:
      '"Internal use only" restrictions kill innovation. You can\'t build products, enhance services, or truly own your data strategy.',
  },
  {
    icon: 'alarm-clock',
    title: 'Outdated Information',
    description:
      'Reports can be months old by the time you see them. In fast-moving markets, stale data leads to costly mistakes.',
  },
  {
    icon: 'rate-hikes',
    title: 'Opaque Pricing & Rate Hikes',
    description: 'Contracts with unclear pricing structures and unpredictable yearly price hikes.',
  },
  {
    icon: 'poor-source',
    title: 'Poor Source Traceability',
    description:
      "Black box data sourcing with no transparency. You can't verify accuracy or understand where information comes from, creating compliance risks.",
  },
  {
    icon: 'slow-support',
    title: 'Slow and Bureaucratic Support',
    description:
      'Weeks for simple requests. Multiple layers of approval for basic changes. When you need answers fast, they move at corporate speed.',
  },
];

const PAYGPage = () => {
  const { data: content, internationalization } = useContext(ContentContext);

  const [hasClearAdvantageBlock, hasCompanyProfileBlock, hasSwitchReasonBlock] = useMemo(
    () => [
      content.blocks.includes('clear-advantage'),
      content.blocks.includes('company-profile'),
      content.blocks.includes('switch-reason'),
    ],
    [content.blocks],
  );

  const productTypes = useMemo(() => {
    if (content.blocks.includes('payg2-pricing')) {
      return ['payg2_starter', 'payg2_professional', 'payg2_business'];
    }

    return undefined;
  }, [content.blocks]);

  return (
    <div className={styles[`${baseClass}-wrapper`]}>
      <Hero
        title={internationalization['title']}
        description={internationalization['description']}
        features={featuresHero}
      />

      <OurCustomersV2 hasBackground={false} />

      {hasCompanyProfileBlock && (
        <CompanyProfile
          title="Complete B2B Intelligence in One Platform"
          description="Everything you need to identify, research, and engage with prospects worldwide"
        />
      )}

      {hasClearAdvantageBlock && (
        <AdvantageBlock title="The Clear Advantage" description={internationalization['advantageDescription']} />
      )}

      {!hasClearAdvantageBlock && hasSwitchReasonBlock && (
        <InfoBlocks
          theme="light"
          layout="column"
          title={
            <>
              The <span className="accent-words">{internationalization['target']}</span> Problem
            </>
          }
          description="Why are thousands of businesses making the switch? The answer is simple."
          items={dataSwitchReasonInfo}
        />
      )}

      {!hasClearAdvantageBlock && (
        <UseCases
          title="The Global Database Advantage"
          description="First-party data. Real-time updates. Total freedom."
          dataCases={dataCases}
          layout="grid-3-3"
        />
      )}

      <PricingBlock
        id="pricing"
        title="Simple, Transparent Pricing"
        description="Choose the plan that fits your needs. Upgrade or downgrade anytime. All plans include our core features."
        productTypes={productTypes}
        hasFeatureKeys={!!productTypes}
      />

      {productTypes && (
        <SalesBanner
          title={internationalization['sales_banner_title']}
          description={internationalization['sales_banner_description']}
          buttonText={internationalization['sales_banner_button_text']}
        />
      )}

      <VideoOverview videoId={VIDEO_ID} />

      <StatsBlock
        title="The Numbers Don't Lie"
        description="See why thousands of companies have already made the switch"
      />

      <BlockStandards title="We Adhere to the Industry’s Highest Standards of Excellence" theme="dark" />

      <Testimonials
        title="What Our Customers Say"
        description="Real results from companies who made the switch"
        data={dataTestimonial}
      />

      <ComparisonTable
        title={`The Real Cost of ${
          internationalization['target_1']
            ? `using ${internationalization['target_1']}`
            : internationalization['target']
        }`}
        description={`Stop paying ${
          internationalization['target_1'] || internationalization['target']
        } premium for inferior data. See the real cost comparison below.`}
      />

      <InfoBlocks
        title="Complete Business Intelligence Platform"
        description="Why are thousands of businesses making the switch? The answer is simple."
        items={dataInfo}
      />

      <OurAwards
        title="Stay Ahead with Global Database Excellence and Recognition"
        description="We Adhere to the Industry's Highest Standards of Excellence"
        variant="split"
      />

      <Faq data={PAYG_SECOND_PAGE_FAQ_DATA} />
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default PAYGPage;
